import { useEffect, useMemo, useState } from 'react';
import { Button } from '@upmc/ims.component-library';
import WithLoadingHandler from '../../../../hoc/withLoadingHandler/withLoadingHandler';
import Section from '../../components/NewApplication/Section';
import { GetApplicationsInfoIndex } from '../../../ApplicationInformation/ApplicationInformation_api';
import { GetAppDependencyPairs, UpdateAppTables, GetApplicationManagementByID } from '../../_api';
import AppManagementRequest from 'Classes/ResponseModels/ApplicationManagement/AppManagementRequest';
import AppManagementModel from 'Classes/ResponseModels/ApplicationManagement/AppManagementModel';
import styles from './AppDependencyManagement.module.scss';

const AppDependencyManagement = ({
	appID,
	currentUser,
	requirementNumber,
	readOnly,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [allApps, setAllApps] = useState([]);
	const [selectedDependencyIds, setSelectedDependencyIds] = useState([]);
	const [savedDependencyIds, setSavedDependencyIds] = useState([]);
	const [sourceSelection, setSourceSelection] = useState([]);
	const [targetSelection, setTargetSelection] = useState([]);
	const [submitMessage, setSubmitMessage] = useState('');
	const [loadMessage, setLoadMessage] = useState('');

	useEffect(() => {
		const loadApps = async () => {
			setIsLoading(true);
			setLoadMessage('');
			setSubmitMessage('');
			setSourceSelection([]);
			setTargetSelection([]);

			try {
				const response = await GetApplicationsInfoIndex();
				const currentAppId = parseInt(appID, 10);
				const indexedApps =
					response?.applications
						?.map(app => {
							const id = parseInt(app?.id, 10);

							return {
								id,
								name: app?.name ?? app?.appInfo?.applicationName ?? '',
							};
						})
						?.filter(
							app => Number.isInteger(app.id) && app.id !== currentAppId
						)
						?.sort((a, b) =>
							(a.name || '').localeCompare(b.name || '')
						) ?? [];

				let allAppsForSelection = [...indexedApps];
				let appIdSet = new Set(allAppsForSelection.map(app => app.id));
				let existingDependencyIds = [];

				try {
					const existingDependencies =
						(await GetAppDependencyPairs(appID)) ?? [];
					const dependencyList = Array.isArray(existingDependencies)
						? existingDependencies
						: existingDependencies?.payload ??
						  existingDependencies?.data ??
						  [];
					const dependencyApps = dependencyList
						.map(item => {
							const id = parseInt(item?.Key ?? item?.key, 10);

							return {
								id,
								name:
									item?.Value ??
									item?.value ??
									`Application ${id}`,
							};
						})
						.filter(
							app => Number.isInteger(app.id) && app.id !== currentAppId
						);

					if (dependencyApps.length > 0) {
						const appsById = new Map(
							allAppsForSelection.map(app => [app.id, app])
						);

						dependencyApps.forEach(app => {
							if (!appsById.has(app.id)) {
								appsById.set(app.id, app);
							}
						});

						allAppsForSelection = Array.from(appsById.values()).sort(
							(a, b) => (a.name || '').localeCompare(b.name || '')
						);
						appIdSet = new Set(allAppsForSelection.map(app => app.id));
					}

					existingDependencyIds = dependencyList
						.map(item =>
							parseInt(item?.Key ?? item?.key, 10)
						)
						.filter(
							id => Number.isInteger(id) && appIdSet.has(id)
						);
				} catch (dependencyError) {
					const status = dependencyError?.response?.status;
					const statusText = dependencyError?.response?.statusText;
					const detail =
						status || statusText
							? ` (${status ?? ''}${statusText ? ` ${statusText}` : ''})`
							: '';

					setLoadMessage(
						`Loaded applications, but unable to preload required applications${detail}.`
					);
				}

				setAllApps(allAppsForSelection);
				setSelectedDependencyIds(existingDependencyIds);
				setSavedDependencyIds(existingDependencyIds);
			} catch (ex) {
				setAllApps([]);
				setLoadMessage('Unable to load applications for required app selection.');
			} finally {
				setIsLoading(false);
			}
		};

		if (appID) {
			loadApps();
		} else {
			setAllApps([]);
			setSelectedDependencyIds([]);
			setSavedDependencyIds([]);
			setLoadMessage('Select an application first.');
			setIsLoading(false);
		}
	}, [appID]);

	const selectedDependenciesSet = useMemo(
		() => new Set(selectedDependencyIds),
		[selectedDependencyIds]
	);

	const sourceApps = useMemo(
		() => allApps.filter(app => !selectedDependenciesSet.has(app.id)),
		[allApps, selectedDependenciesSet]
	);

	const targetApps = useMemo(
		() => allApps.filter(app => selectedDependenciesSet.has(app.id)),
		[allApps, selectedDependenciesSet]
	);

	const mapSelectionValues = event =>
		Array.from(event.target.selectedOptions).map(option =>
			parseInt(option.value, 10)
		);

	const addSelected = () => {
		setSelectedDependencyIds(prev =>
			Array.from(new Set(prev.concat(sourceSelection)))
		);
		setSourceSelection([]);
	};

	const removeSelected = () => {
		const toRemove = new Set(targetSelection);
		setSelectedDependencyIds(prev => prev.filter(id => !toRemove.has(id)));
		setTargetSelection([]);
	};

	const handleSubmit = e => {
		e.preventDefault();
		
		if (!appID) {
			setSubmitMessage('No application selected.');
			return;
		}

		setIsSubmitting(true);
		setSubmitMessage('');

		// Fetch the full app object first to ensure all properties are present
		GetApplicationManagementByID(appID)
			.then(fullApp => {
				if (!fullApp || fullApp.error) {
					setSubmitMessage('Error: Unable to load application data.');
					setIsSubmitting(false);
					return;
				}

				// Create dependency objects with requestedAppId and dependentAppId
				const dependencyPairs = selectedDependencyIds.map(depId => ({
					requestedAppId: parseInt(appID, 10),
					dependentAppId: depId
				}));

				// Update the full app object with new dependencies
				fullApp.dependencies = dependencyPairs;

				// Build request object matching AppManagementRequest structure
				const request = new AppManagementRequest(fullApp, currentUser, requirementNumber);

				// Call the UpdateAppTables API
				return UpdateAppTables(request);
			})
			.then(response => {
				if (response && response.Errors && response.Errors.length > 0) {
					setSubmitMessage(`Error: ${response.Errors.join(', ')}`);
				} else {
					setSavedDependencyIds([...selectedDependencyIds]);
					setSubmitMessage('Dependencies updated successfully');
				}
				setIsSubmitting(false);
			})
			.catch(error => {
				setSubmitMessage(`Error submitting dependencies: ${error.message}`);
				setIsSubmitting(false);
			});
	};


	return (
		<>
			<WithLoadingHandler
				isLoading={isLoading}
				showChildren={!isLoading}
			>
				<Section label='App Dependencies'>
					<div className={styles.container}>
						<form className={styles.form} onSubmit={handleSubmit}>
					<div className={styles.listSection}>
						<label htmlFor='appDependencySource' className={styles.label}>
							Application(s)
						</label>
						<select
							id='appDependencySource'
							multiple
							className={styles.listBox}
							value={sourceSelection.map(String)}
							onChange={e =>
								setSourceSelection(mapSelectionValues(e))
							}
							disabled={readOnly}
						>
							{sourceApps.map(app => (
								<option key={app.id} value={app.id}>
									{app.name}
								</option>
							))}
						</select>
					</div>

					<div className={styles.actionsSection}>
						<Button
							type='button'
							onClick={addSelected}
							disabled={readOnly || sourceSelection.length === 0}
						>
							&gt;
						</Button>
						<Button
							type='button'
							onClick={removeSelected}
							disabled={readOnly || targetSelection.length === 0}
						>
							&lt;
						</Button>
					</div>

					<div className={styles.listSection}>
						<label htmlFor='appDependencyTarget' className={styles.label}>
							Required Application(s)
						</label>
						<select
							id='appDependencyTarget'
							multiple
							className={styles.listBox}
							value={targetSelection.map(String)}
							onChange={e =>
								setTargetSelection(mapSelectionValues(e))
							}
							disabled={readOnly}
						>
							{targetApps.map(app => (
								<option key={app.id} value={app.id}>
									{app.name}
								</option>
							))}
						</select>
					</div>
				</form>

				<div className={styles.controlsRow}>
					<Button
						type='submit'
						onClick={handleSubmit}
						disabled={readOnly || !appID || isSubmitting}
					>
						Submit
					</Button>
				</div>

				<WithLoadingHandler
					isLoading={isSubmitting}
					showChildren={!isSubmitting && submitMessage}
				>
					{submitMessage && (
						<div className={submitMessage.includes('successfully') ? styles.successMessage : styles.message}>
							{submitMessage}
						</div>
					)}
				</WithLoadingHandler>

				{loadMessage && <div className={styles.message}>{loadMessage}</div>}
				</div>
			</Section>
			</WithLoadingHandler>
		</>
	);
};

export default AppDependencyManagement;